import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';

import { DataGrid, KeenIcon } from '@/components';
import { Container } from '@/components/container';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import {
  useGetLegalComplianceQuery,
  useLazyExportLegalComplianceCsvQuery,
} from '@/services/LegalComplianceService';
import {
  LegalAcceptanceEvent,
  LegalComplianceQueryParams,
} from '@/types/legal-compliance.types';
import { usePermissions } from '@/hooks/usePermissions';
import usePagination from '@/hooks/usePagination';
import { isRouteAllowed } from '@/constant/rolePermissions';
import { useCurrentUserRoles } from '@/utils/userRole';
import { formatFinnishNumber } from '@/utils';
import { formatDisplayDateTime } from '@/utils/Date';
import { formatLegalEffectiveDate } from '@/utils/legalEffectiveDate';

const formatSubjectType = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const toApiDate = (displayDate: string): string | undefined => {
  if (!displayDate || !moment(displayDate, 'DD/MM/YYYY').isValid()) {
    return undefined;
  }
  return moment(displayDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
};

const getAcceptanceSubjectDisplay = (row: LegalAcceptanceEvent) =>
  row.accepted_by_user?.email?.trim() || row.subject_id?.trim() || '—';

const LegalCompliancePage = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const roles = useCurrentUserRoles();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canRead =
    hasAnyPermission(['legal_compliance_read', 'cms_management_read']) ||
    isRouteAllowed('/legal-compliance', roles);
  const canExport =
    hasPermission('legal_compliance_export') ||
    hasPermission('legal_compliance_read');

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [currentVersionOnly, setCurrentVersionOnly] = useState(false);

  const queryParams: LegalComplianceQueryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      startDate: toApiDate(tempStartDate),
      endDate: toApiDate(tempEndDate),
      currentVersionOnly: currentVersionOnly || undefined,
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      tempStartDate,
      tempEndDate,
      currentVersionOnly,
    ]
  );

  const { data, isLoading, isFetching, isError } = useGetLegalComplianceQuery(
    queryParams,
    { skip: !canRead }
  );
  const loadErrorShownRef = useRef(false);
  const [exportCsv, { isLoading: exportLoading }] =
    useLazyExportLegalComplianceCsvQuery();

  const stats = data?.stats;
  const rows: LegalAcceptanceEvent[] = data?.list?.items ?? [];
  const loading = isLoading || isFetching;

  const hasActiveFilters =
    !!tempStartDate || !!tempEndDate || currentVersionOnly;

  useEffect(() => {
    if (data?.list) {
      handleTotalPages(data.list.total_pages || 1);
      handleTotalItems(data.list.total || 0);
    }
  }, [data?.list, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    if (!isError) {
      loadErrorShownRef.current = false;
      return;
    }
    if (loadErrorShownRef.current) {
      return;
    }
    loadErrorShownRef.current = true;
    enqueueSnackbar(
      intl.formatMessage({ id: 'LEGAL_COMPLIANCE.LOAD_FAILED' }),
      { variant: 'error' }
    );
  }, [isError, enqueueSnackbar, intl]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempStartDate, tempEndDate, currentVersionOnly]);

  const resetFilters = () => {
    setTempStartDate('');
    setTempEndDate('');
    setCurrentVersionOnly(false);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  };

  const handleExport = async () => {
    try {
      const csv = await exportCsv(queryParams).unwrap();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.download = `legal-acceptances_${date}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      enqueueSnackbar(
        intl.formatMessage({ id: 'LEGAL_COMPLIANCE.EXPORT_SUCCESS' }),
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar(
        intl.formatMessage({ id: 'LEGAL_COMPLIANCE.EXPORT_FAILED' }),
        { variant: 'error' }
      );
    }
  };

  const metricCards = useMemo(
    () => [
      {
        key: 'Users',
        label: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.METRIC_SUBJECTS' }),
        value:
          loading && !stats
            ? '--'
            : formatFinnishNumber(stats?.unique_subjects_accepted ?? 0, {
                maximumFractionDigits: 0,
              }),
        icon: 'people',
      },
      {
        key: 'events',
        label: intl.formatMessage({
          id: 'LEGAL_COMPLIANCE.METRIC_TERMS_EVENTS',
        }),
        value:
          loading && !stats
            ? '--'
            : formatFinnishNumber(stats?.total_acceptance_events ?? 0, {
                maximumFractionDigits: 0,
              }),
        icon: 'check-circle',
      },
      {
        key: 'version',
        label: intl.formatMessage({
          id: 'LEGAL_COMPLIANCE.METRIC_TERMS_VERSION',
        }),
        value:
          loading && !stats
            ? '--'
            : stats?.current_terms_effective_from
              ? formatLegalEffectiveDate(stats.current_terms_effective_from)
              : stats?.current_terms_version
                ? `v${stats.current_terms_version}`
                : '—',
        icon: 'document',
      },
    ],
    [intl, loading, stats]
  );

  const columns = useMemo(
    () => [
      {
        accessorFn: (row: LegalAcceptanceEvent) => row.accepted_at,
        id: 'accepted_at',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_ACCEPTED_AT' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <span className="text-sm text-gray-900">
            {formatDisplayDateTime(row.original.accepted_at)}
          </span>
        ),
      },
      {
        accessorFn: (row: LegalAcceptanceEvent) => row.document_type,
        id: 'document_type',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_DOCUMENT' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <span className="text-sm font-medium text-gray-900">
            {row.original.document_type === 'privacy'
              ? intl.formatMessage({ id: 'LEGAL_COMPLIANCE.PRIVACY' })
              : intl.formatMessage({ id: 'LEGAL_COMPLIANCE.TERMS' })}
          </span>
        ),
      },
      {
        accessorFn: (row: LegalAcceptanceEvent) => row.document_effective_from,
        id: 'document_effective_from',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_VERSION' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <span className="text-sm text-gray-900">
            {row.original.document_effective_from
              ? formatLegalEffectiveDate(row.original.document_effective_from)
              : `v${row.original.document_version}`}
          </span>
        ),
      },
      {
        accessorFn: (row: LegalAcceptanceEvent) => row.is_current_document,
        id: 'is_current_document',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_CURRENT' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <span
            className={`text-sm font-medium ${
              row.original.is_current_document
                ? 'text-green-700 contrast:text-green-300'
                : 'text-amber-700 contrast:text-amber-300'
            }`}
          >
            {row.original.is_current_document
              ? intl.formatMessage({ id: 'LEGAL_COMPLIANCE.CURRENT' })
              : intl.formatMessage({ id: 'LEGAL_COMPLIANCE.OUTDATED' })}
          </span>
        ),
      },
      {
        accessorFn: (row: LegalAcceptanceEvent) =>
          getAcceptanceSubjectDisplay(row),
        id: 'subject',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_SUBJECT' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">
              {formatSubjectType(row.original.subject_type)}
            </span>
            <span className="text-xs text-gray-500">
              {getAcceptanceSubjectDisplay(row.original)}
            </span>
          </div>
        ),
      },
      {
        accessorFn: (row: LegalAcceptanceEvent) => row.context,
        id: 'context',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_CONTEXT' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <span className="text-sm text-gray-700">
            {formatSubjectType(row.original.context)}
          </span>
        ),
      },
      {
        accessorFn: (row: LegalAcceptanceEvent) => row.language,
        id: 'language',
        header: intl.formatMessage({ id: 'LEGAL_COMPLIANCE.COL_LANGUAGE' }),
        enableSorting: false,
        cell: ({ row }: { row: { original: LegalAcceptanceEvent } }) => (
          <span className="text-sm uppercase text-gray-700">
            {row.original.language ?? '—'}
          </span>
        ),
      },
    ],
    [intl]
  );

  if (!canRead) {
    return null;
  }

  return (
    <Container>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1
            id="legal-compliance-heading"
            className="text-xl font-semibold text-gray-900"
            tabIndex={-1}
          >
            {intl.formatMessage({ id: 'LEGAL_COMPLIANCE.PAGE_TITLE' })}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {intl.formatMessage({ id: 'LEGAL_COMPLIANCE.PAGE_DESCRIPTION' })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          <div className="flex-shrink-0">
            <DateRangePicker
              startDateName="startDate"
              endDateName="endDate"
              isMandatory={false}
              value={[tempStartDate, tempEndDate]}
              onChange={([newStartDate, newEndDate]) => {
                handleDateRangeChange(
                  newStartDate as string,
                  newEndDate as string
                );
              }}
              allowPastDates
              allowFutureDates={false}
              className="h-8 w-full text-sm sm:h-10 sm:w-64"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="checkbox checkbox-sm cursor-pointer"
              checked={currentVersionOnly}
              onChange={(e) => setCurrentVersionOnly(e.target.checked)}
            />
            {intl.formatMessage({
              id: 'LEGAL_COMPLIANCE.CURRENT_VERSION_ONLY',
            })}
          </label>

          {hasActiveFilters && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2"
              onClick={resetFilters}
              title={intl.formatMessage({
                id: 'LEGAL_COMPLIANCE.CLEAR_FILTERS',
              })}
            >
              {intl.formatMessage({ id: 'LEGAL_COMPLIANCE.CLEAR_FILTERS' })}
            </button>
          )}

          {canExport && (
            <button
              type="button"
              className="btn btn-sm btn-primary whitespace-nowrap"
              disabled={exportLoading}
              onClick={handleExport}
            >
              {exportLoading
                ? intl.formatMessage({ id: 'LEGAL_COMPLIANCE.EXPORTING' })
                : intl.formatMessage({ id: 'LEGAL_COMPLIANCE.EXPORT_CSV' })}
            </button>
          )}
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {metricCards.map((card) => (
              <div key={card.key} className="rounded-lg bg-gray-100 p-5 shadow">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-gray-500">{card.label}</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {card.value}
                    </p>
                  </div>
                  <KeenIcon
                    icon={card.icon}
                    className="text-2xl text-primary"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <DataGrid
            columns={columns}
            data={rows}
            rowSelect={false}
            loading={loading}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: '{from} - {to} of {count}',
              sizes: [10, 20, 25, 50, 100],
              sizesLabel: intl.formatMessage({
                id: 'COMMON.PAGINATION.SHOW',
              }),
              sizesDescription: intl.formatMessage({
                id: 'COMMON.PAGINATION.PER_PAGE',
              }),
            }}
            serverSide
            onFilterReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
            aria-label={intl.formatMessage({ id: 'LEGAL_COMPLIANCE.RECORDS' })}
            messages={{
              empty: intl.formatMessage({
                id: 'LEGAL_COMPLIANCE.EMPTY_RECORDS',
              }),
            }}
          />
        </div>
      </div>
    </Container>
  );
};

export { LegalCompliancePage };
